import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Button, Card, CardBody, Col, Container, Input, Row, Spinner } from "reactstrap";
import { ErrorToaster, successToaster } from "components/common/toast";
import handleError from "utils/handleError";
import { AIStructureAPI, GetStructureAPI, SaveStructureAPI } from "network/menuCards";
import AiPromptModal from "./AiPromptModal";
import EditItemModal from "./EditItemModal";
import { kindT } from "./cardKind";
import {
  UNASSIGNED,
  addCategory,
  addSub,
  categoryContainer,
  countProducts,
  emptyNameKey,
  fromApi,
  itemsOf,
  moveCategory,
  moveItem,
  moveSub,
  removeNode,
  renameNode,
  shift,
  subContainer,
  toApi,
  toggleNode,
  toggleProduct,
} from "./structureTree";

const GOLD = "#b8932f";
const STYLE = `
.wc-block{border:1px solid rgba(128,128,128,.35);border-radius:10px;padding:10px 12px;margin-bottom:12px;background:rgba(255,255,255,.55)}
.wc-sub{margin:10px 0 0 22px;background:rgba(0,0,0,.03)}
.wc-head{display:flex;align-items:center;gap:6px;flex-wrap:wrap}
.wc-head .wc-name{flex:1;min-width:160px}
.wc-head .btn{margin:0;padding:6px 10px}
.wc-handle{cursor:grab;font-size:1.3rem;line-height:1;padding:0 6px;user-select:none;color:${GOLD}}
.wc-off{opacity:.55}
.wc-list{min-height:34px;margin:8px 0 2px;padding:2px;border-radius:8px}
.wc-item{display:flex;justify-content:space-between;gap:8px;align-items:center;padding:6px 10px;margin:3px 0;border:1px solid rgba(128,128,128,.3);border-radius:8px;background:#fff;color:#333;cursor:grab;font-size:.9rem}
.wc-item small{color:#777;white-space:nowrap}
.wc-empty{color:#999;font-size:.82rem;padding:6px 8px}
.wc-before{box-shadow:0 -3px 0 0 ${GOLD}}
.wc-after{box-shadow:0 3px 0 0 ${GOLD}}
.wc-into{outline:2px dashed ${GOLD};outline-offset:2px}
.wc-side{position:sticky;top:90px}
.wc-item .wc-txt{min-width:0;flex:1}
.wc-item .wc-desc{display:block;color:#888;font-size:.78rem;white-space:normal}
.wc-item .wc-end{display:flex;align-items:center;gap:8px;flex:none}
.wc-item .wc-edit{padding:3px 10px;font-size:.78rem;margin:0}
.wc-item.wc-hidden{border-style:dashed;opacity:.6}
.wc-hide-tag{background:rgba(128,128,128,.18);color:#555;border-radius:8px;padding:1px 7px;font-size:.7rem;margin-left:6px;white-space:nowrap}
.wc-local{background:#f3e6bf;color:#5c4710;border-radius:8px;padding:1px 7px;font-size:.7rem;margin-left:6px;white-space:nowrap}
`;

const priceLabel = (p) => (p.price != null && p.price !== "" ? `€ ${p.price}` : "");
const isBefore = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  return e.clientY < r.top + r.height / 2;
};

const StructureEditor = () => {
  const { t: baseT, i18n } = useTranslation("cards");
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [meta, setMeta] = useState({ name: "", structured: true, kind: "drinks" });
  // Food Card: i testi parlano di piatti e menu invece che di vini.
  const t = kindT(baseT, i18n, meta.kind === "food");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [hover, setHover] = useState(null); // dove cadrebbe l'elemento trascinato (solo per evidenziarlo)
  const [focusKey, setFocusKey] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [editing, setEditing] = useState(null); // voce aperta in "Modifica"
  const dragRef = useRef(null);
  const hoverRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await GetStructureAPI(id);
      setState(fromApi(res?.data));
      setMeta({ name: res?.data?.name || "", structured: !!res?.data?.structured, kind: res?.data?.kind || "drinks" });
      setDirty(false);
    } catch (e) {
      ErrorToaster(handleError(e?.status_code, e?.message));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Avvisa se si chiude la pagina con modifiche non salvate.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // Dopo "Modifica" il piatto e' gia' salvato: si aggiorna ovunque compare, senza segnare la struttura come modificata.
  const replaceItem = (updated) => {
    if (!updated) return;
    const swap = (list) => list.map((p) => (String(p.uuid) === String(updated.uuid) ? { ...p, ...updated } : p));
    setState((s) => ({
      ...s,
      unassigned: swap(s.unassigned),
      categories: s.categories.map((c) => ({ ...c, products: swap(c.products), children: c.children.map((x) => ({ ...x, products: swap(x.products) })) })),
    }));
  };

  const change = (fn) => {
    setState(fn);
    setDirty(true);
  };

  const goBack = () => {
    if (dirty && !window.confirm(t("structure.unsavedConfirm"))) return;
    navigate("/menu-cards");
  };

  const save = async () => {
    const bad = emptyNameKey(state);
    if (bad) {
      setFocusKey(bad);
      ErrorToaster(t("structure.nameAll"));
      return;
    }
    setSaving(true);
    try {
      const res = await SaveStructureAPI(id, toApi(state));
      setState(fromApi(res?.data));
      setMeta((m) => ({ ...m, structured: true }));
      setDirty(false);
      successToaster(t("structure.saved"));
    } catch (e) {
      ErrorToaster(handleError(e?.status_code, e?.message));
    } finally {
      setSaving(false);
    }
  };

  // Proposta dell'AI: sostituisce la struttura che si vede, ma non salva finche' non si preme "Salva struttura".
  const askAI = async (instruction) => {
    if (countProducts(state) === 0) {
      ErrorToaster(t("structure.noWines"));
      return false;
    }
    if (
      (meta.structured || dirty) &&
      !window.confirm(t("structure.aiConfirm"))
    ) {
      return false;
    }
    try {
      const res = await AIStructureAPI(id, instruction);
      setState(fromApi(res?.data));
      setDirty(true);
      successToaster(res?.message || t("structure.proposalReady"));
      return true;
    } catch (e) {
      ErrorToaster(handleError(e?.status_code, e?.message));
      return false;
    }
  };

  const addCat = () => {
    const r = addCategory(state, "");
    setState(r.state);
    setDirty(true);
    setFocusKey(r.key);
  };
  const addSubTo = (categoryKey) => {
    const r = addSub(state, categoryKey, "");
    setState(r.state);
    setDirty(true);
    setFocusKey(r.key);
  };
  const confirmRemove = (node) => {
    const count = (node.products?.length || 0) + (node.children || []).reduce((n, s) => n + s.products.length, 0);
    const name = node.name || t("structure.unnamed");
    const text = count ? t("structure.removeWithWines", { name, count }) : t("structure.remove", { name });
    if (window.confirm(text)) change((s) => removeNode(s, node.key));
  };

  // ---- Trascinamento (HTML5): si trascina la maniglia di categorie e sottocategorie, o la riga di un vino ----
  const setTarget = (target, show) => {
    hoverRef.current = target;
    setHover((prev) => (JSON.stringify(prev) === JSON.stringify(show) ? prev : show));
  };
  const clearDrag = () => {
    dragRef.current = null;
    hoverRef.current = null;
    setHover(null);
  };
  const dragSource = (type, key, imageSelector) => ({
    draggable: true,
    onDragStart: (e) => {
      e.stopPropagation();
      dragRef.current = { type, key };
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", key);
      const block = imageSelector && e.currentTarget.closest(imageSelector);
      if (block && e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(block, 14, 14);
    },
    onDragEnd: clearDrag,
  });

  const onCategoryOver = (cat) => (e) => {
    const d = dragRef.current;
    if (!d) return;
    if (d.type === "cat" && d.key !== cat.key) {
      e.preventDefault();
      e.stopPropagation();
      const before = isBefore(e);
      const i = state.categories.findIndex((c) => c.key === cat.key);
      const beforeKey = before ? cat.key : state.categories[i + 1]?.key ?? null;
      setTarget({ type: "cat", beforeKey }, { kind: "cat", key: cat.key, pos: before ? "before" : "after" });
    } else if (d.type === "sub") {
      // Una sottocategoria lasciata sulla categoria va in fondo alle sue sottocategorie.
      e.preventDefault();
      e.stopPropagation();
      setTarget({ type: "sub", toCat: cat.key, beforeKey: null }, { kind: "into", key: cat.key });
    }
  };
  const onSubOver = (cat, sub) => (e) => {
    const d = dragRef.current;
    if (!d || d.type !== "sub" || d.key === sub.key) return;
    e.preventDefault();
    e.stopPropagation();
    const before = isBefore(e);
    const i = cat.children.findIndex((s) => s.key === sub.key);
    const beforeKey = before ? sub.key : cat.children[i + 1]?.key ?? null;
    setTarget({ type: "sub", toCat: cat.key, beforeKey }, { kind: "sub", key: sub.key, pos: before ? "before" : "after" });
  };
  const onBoxOver = (container) => (e) => {
    const d = dragRef.current;
    if (!d || d.type !== "prod") return;
    e.preventDefault();
    e.stopPropagation();
    setTarget(
      { type: "prod", container, index: itemsOf(state, container).length },
      { kind: "box", key: container }
    );
  };
  const onRowOver = (container, index, item) => (e) => {
    const d = dragRef.current;
    if (!d || d.type !== "prod") return;
    e.preventDefault();
    e.stopPropagation();
    const before = isBefore(e);
    setTarget({ type: "prod", container, index: before ? index : index + 1 }, { kind: "prod", key: item.uuid, pos: before ? "before" : "after" });
  };
  const onDrop = (e) => {
    const d = dragRef.current;
    const h = hoverRef.current;
    if (!d || !h || d.type !== h.type) return;
    e.preventDefault();
    if (d.type === "cat") change((s) => moveCategory(s, d.key, h.beforeKey));
    else if (d.type === "sub") change((s) => moveSub(s, d.key, h.toCat, h.beforeKey));
    else change((s) => moveItem(s, d.key, h.container, h.index));
    clearDrag();
  };

  const mark = (kind, key) => {
    if (!hover || hover.key !== key) return "";
    if (kind === "cat" && hover.kind === "cat") return hover.pos === "before" ? "wc-before" : "wc-after";
    if (kind === "cat" && hover.kind === "into") return "wc-into";
    if (kind === "sub" && hover.kind === "sub") return hover.pos === "before" ? "wc-before" : "wc-after";
    if (kind === "prod" && hover.kind === "prod") return hover.pos === "before" ? "wc-before" : "wc-after";
    if (kind === "box" && hover.kind === "box") return "wc-into";
    return "";
  };

  const renderItems = (container, items, emptyText) => (
    <div className={`wc-list ${mark("box", container)}`} onDragOver={onBoxOver(container)}>
      {items.length === 0 && <div className="wc-empty">{emptyText}</div>}
      {items.map((p, i) => (
        <div
          key={p.uuid}
          className={`wc-item ${p.is_hidden ? "wc-hidden" : ""} ${mark("prod", p.uuid)}`}
          onDragOver={onRowOver(container, i, p)}
          {...dragSource("prod", p.uuid)}
        >
          <span className="wc-txt">
            {p.name}
            {p.vintage ? ` ${parseInt(p.vintage, 10)}` : ""}
            {p.local && <span className="wc-local">{t("structure.edit.localBadge")}</span>}
            {p.is_hidden && <span className="wc-hide-tag">{t("structure.hiddenTag")}</span>}
            {p.is_available === false && <span className="badge badge-warning ml-2">{t("structure.soldOut")}</span>}
            {!p.is_wine && p.description && <small className="wc-desc">{p.description}</small>}
          </span>
          <span className="wc-end">
            <small>{priceLabel(p)}</small>
            <Button
              size="sm"
              color="secondary"
              className="wc-edit"
              draggable={false}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); setEditing(p); }}
            >
              {t("structure.edit.button")}
            </Button>
            <Button
              size="sm"
              color="info"
              className="wc-edit"
              draggable={false}
              title={p.is_hidden ? t("structure.showItemTitle") : t("structure.hideItemTitle")}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); change((s) => toggleProduct(s, p.uuid)); }}
            >
              {p.is_hidden ? t("structure.show") : t("structure.hide")}
            </Button>
          </span>
        </div>
      ))}
    </div>
  );

  const renderHead = (node, placeholder, handleSelector, type) => {
    const n = (node.products?.length || 0) + (node.children || []).reduce((sum, c) => sum + c.products.length, 0);
    return (
    <div className="wc-head">
      <span className="wc-handle" title={t("structure.dragTitle")} {...dragSource(type, node.key, handleSelector)}>⠿</span>
      <Input
        className="wc-name"
        value={node.name}
        placeholder={placeholder}
        autoFocus={focusKey === node.key}
        onFocus={() => focusKey === node.key && setFocusKey(null)}
        onChange={(e) => change((s) => renameNode(s, node.key, e.target.value))}
      />
      <span className="badge badge-secondary" title={t("structure.winesInItem")}>{t("structure.wineCount", { count: n })}</span>
      <Button size="sm" color="secondary" title={t("structure.moveUp")} onClick={() => change((s) => shift(s, node.key, -1))}>↑</Button>
      <Button size="sm" color="secondary" title={t("structure.moveDown")} onClick={() => change((s) => shift(s, node.key, 1))}>↓</Button>
      <Button size="sm" color="info" onClick={() => change((s) => toggleNode(s, node.key))}>
        {node.is_active ? t("structure.hide") : t("structure.show")}
      </Button>
      <Button size="sm" color="danger" onClick={() => confirmRemove(node)}>{t("structure.delete")}</Button>
    </div>
    );
  };

  if (loading) {
    return (
      <div className="content text-center py-5"><Spinner /></div>
    );
  }
  if (!state) {
    return (
      <div className="content">
        <Container fluid>
          <Button color="secondary" onClick={() => navigate("/menu-cards")}>{t("structure.back")}</Button>
        </Container>
      </div>
    );
  }

  return (
    <div
      className="content"
      // Ad ogni passaggio si azzera la destinazione: la imposta di nuovo l'elemento sotto il mouse, se accetta il rilascio.
      onDragOverCapture={() => {
        hoverRef.current = null;
        setHover(null);
      }}
      onDrop={onDrop}
    >
      <style>{STYLE}</style>
      <Container fluid>
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <Button color="secondary" className="mr-3 mb-0" onClick={goBack}>{t("structure.back")}</Button>
            <h2 className="mb-0">{t("structure.title")}{meta.name ? `: ${meta.name}` : ""}</h2>
            <span className="badge badge-primary ml-3" style={{ fontSize: "0.9rem" }}>
              {t("structure.inCard", { count: countProducts(state) })}
            </span>
          </div>
          <div className="d-flex align-items-center">
            <Button color="info" className="mb-0 mr-2" onClick={() => setAiOpen(true)}>{t("structure.organizeAi")}</Button>
            <Button color="secondary" className="mb-0 mr-2" onClick={addCat}>{t("structure.addCategory")}</Button>
            <Button color="secondary" className="mb-0 mr-2" disabled={!dirty || saving} onClick={load}>{t("structure.undo")}</Button>
            <Button color="primary" className="mb-0" disabled={saving || (!dirty && meta.structured)} onClick={save}>
              {saving ? t("saving") : t("structure.save")}
            </Button>
          </div>
        </div>

        {!meta.structured && (
          <Alert color="info">
            {t("structure.proposedInfo")}
          </Alert>
        )}

        {editing && <EditItemModal cardId={id} item={editing} onClose={() => setEditing(null)} onSaved={replaceItem} />}
        <AiPromptModal
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          title={t("structure.ai.title")}
          intro={t("structure.ai.intro")}
          placeholder={t("structure.ai.placeholder")}
          examples={t("structure.ai.examples", { returnObjects: true })}
          buttonLabel={t("structure.ai.button")}
          busyLabel={t("structure.ai.busy")}
          onSubmit={askAI}
        />

        <Row>
          <Col lg="8">
            {state.categories.length === 0 && (
              <Card><CardBody className="text-center text-muted py-5">
                {t("structure.noCategories")}
              </CardBody></Card>
            )}
            {state.categories.map((cat) => (
              <div
                key={cat.key}
                data-drag-block
                className={`wc-block ${cat.is_active ? "" : "wc-off"} ${mark("cat", cat.key)}`}
                onDragOver={onCategoryOver(cat)}
              >
                {renderHead(cat, t("structure.categoryPlaceholder"), "[data-drag-block]", "cat")}
                {renderItems(categoryContainer(cat.key), cat.products, t("structure.emptyCategory"))}
                {cat.children.map((sub) => (
                  <div
                    key={sub.key}
                    data-drag-sub
                    className={`wc-block wc-sub ${sub.is_active ? "" : "wc-off"} ${mark("sub", sub.key)}`}
                    onDragOver={onSubOver(cat, sub)}
                  >
                    {renderHead(sub, t("structure.subPlaceholder"), "[data-drag-sub]", "sub")}
                    {renderItems(subContainer(sub.key), sub.products, t("structure.emptySub"))}
                  </div>
                ))}
                <Button size="sm" color="link" className="p-0 mt-2" onClick={() => addSubTo(cat.key)}>
                  {t("structure.addSub")}
                </Button>
              </div>
            ))}
          </Col>
          <Col lg="4">
            <div className="wc-side">
              <Card>
                <CardBody>
                  <h4 className="mb-1">{t("structure.unassignedTitle", { n: state.unassigned.length })}</h4>
                  <p className="text-muted small mb-2">
                    {t("structure.unassignedHelp")}
                  </p>
                  {renderItems(UNASSIGNED, state.unassigned, t("structure.allPlaced"))}
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default StructureEditor;
